import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import {
  ADMIN_DATETIME_HOUR_OPTIONS,
  ADMIN_DATETIME_MINUTE_OPTIONS,
  type AdminDateTimeSelectParts,
  type Meridiem,
  buildAdminDateTimeIsoFromSelectParts,
  formatAdminDateTimePreview,
  formatAdminDateTimeSelectParts,
  normalizeMeridiem,
} from '../../utils/datetimeInput'

interface AdminDateRangeCalendarRowProps {
  startDate: string | null
  endDate: string | null
  onChange: (startDate: string | null, endDate: string | null) => void
  startLabel?: string
  endLabel?: string
}

const dateToCalendarString = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const calendarStringToDate = (value: string): Date | undefined => {
  if (!value) return undefined
  const [year, month, day] = value.split('-').map(Number)
  if (!year || !month || !day) return undefined
  return new Date(year, month - 1, day)
}

const TimeSelect = ({
  label,
  parts,
  onChange,
}: {
  label: string
  parts: AdminDateTimeSelectParts
  onChange: (parts: AdminDateTimeSelectParts) => void
}) => (
  <div className="admin-date-range-row__time-select">
    <select
      className="admin-central-datetime-field__select"
      value={parts.hour}
      disabled={!parts.date}
      onChange={event => onChange({ ...parts, hour: event.target.value })}
      aria-label={`${label} hour`}
    >
      {ADMIN_DATETIME_HOUR_OPTIONS.map(hour => (
        <option key={hour} value={hour}>
          {hour}
        </option>
      ))}
    </select>
    <span className="admin-central-datetime-field__separator">:</span>
    <select
      className="admin-central-datetime-field__select"
      value={parts.minute}
      disabled={!parts.date}
      onChange={event => onChange({ ...parts, minute: event.target.value })}
      aria-label={`${label} minute`}
    >
      {ADMIN_DATETIME_MINUTE_OPTIONS.map(minute => (
        <option key={minute} value={minute}>
          {minute}
        </option>
      ))}
    </select>
    <select
      className="admin-central-datetime-field__select admin-central-datetime-field__meridiem"
      value={parts.meridiem}
      disabled={!parts.date}
      onChange={event => onChange({ ...parts, meridiem: normalizeMeridiem(event.target.value) as Meridiem })}
      aria-label={`${label} AM or PM`}
    >
      <option value="am">AM</option>
      <option value="pm">PM</option>
    </select>
  </div>
)

const AdminDateRangeCalendarRow = ({ startDate, endDate, onChange, startLabel = 'Starts', endLabel = 'Ends' }: AdminDateRangeCalendarRowProps) => {
  const startParts = formatAdminDateTimeSelectParts(startDate)
  const endParts = formatAdminDateTimeSelectParts(endDate)

  const emit = (nextStart: AdminDateTimeSelectParts, nextEnd: AdminDateTimeSelectParts) => {
    onChange(buildAdminDateTimeIsoFromSelectParts(nextStart), buildAdminDateTimeIsoFromSelectParts(nextEnd))
  }

  // react-day-picker's own range mode keeps `from` fixed and only moves `to` once a
  // complete range exists (its default addToRange behavior). Admins expect each new
  // click-pair to start a fresh range instead, so the state machine is handled here
  // using the actually-clicked day rather than the library's computed range.
  const handleDayClick = (day: Date) => {
    const clickedDate = dateToCalendarString(day)
    const hasCompleteRange = !!startParts.date && !!endParts.date

    if (!startParts.date || hasCompleteRange) {
      emit({ ...startParts, date: clickedDate }, { ...endParts, date: '' })
      return
    }

    // one date already picked — this click completes the range (swap if picked before the start)
    if (clickedDate < startParts.date) {
      emit({ ...startParts, date: clickedDate }, { ...endParts, date: startParts.date })
    } else {
      emit({ ...startParts, date: startParts.date }, { ...endParts, date: clickedDate })
    }
  }

  const handleClearRange = () => {
    onChange(null, null)
  }

  const startPreview = formatAdminDateTimePreview(startDate)
  const endPreview = formatAdminDateTimePreview(endDate)

  return (
    <div className="admin-date-range-row">
      <div className="admin-date-range-row__calendar-header">
        {(startParts.date || endParts.date) && (
          <button type="button" className="admin-date-range-row__clear-link" onClick={handleClearRange}>
            Clear dates
          </button>
        )}
      </div>

      <div className="admin-date-range-row__calendar">
        <DayPicker
          mode="range"
          selected={{ from: calendarStringToDate(startParts.date), to: calendarStringToDate(endParts.date) }}
          onSelect={(_range, day) => handleDayClick(day)}
        />
      </div>

      <div className="admin-date-range-row__times">
        <div className="admin-date-range-row__time-line">
          <label className="admin-date-range-row__time-label">{startLabel}</label>
          <TimeSelect label={startLabel} parts={startParts} onChange={next => emit(next, endParts)} />
        </div>
        <div className="admin-date-range-row__time-line">
          <label className="admin-date-range-row__time-label">{endLabel}</label>
          <TimeSelect label={endLabel} parts={endParts} onChange={next => emit(startParts, next)} />
        </div>
      </div>

      {startPreview ? (
        <p className="admin-date-range-row__preview">
          {startPreview}
          {endPreview && endPreview !== startPreview ? <> &rarr; {endPreview}</> : null}
        </p>
      ) : (
        <p className="admin-central-datetime-field__hint">Pick a date on the calendar above, then set the time in Central Time.</p>
      )}
    </div>
  )
}

export default AdminDateRangeCalendarRow
